import { useState, useEffect } from 'react'
import TrainQueueEditableCell from './TrainQueueEditableCell'

export const TRAIN_QUEUE_STATUS = {
    ESTACIONADO:           { label: 'Estacionado / cadenciado fora do Porto', short: 'Estacionado',      color: '#e8667a', bg: '#fdecef', text: '#a3293d' },
    PROGRAMADO_ESTACIONAR: { label: 'Programado estacionar',                  short: 'Prog. estacionar', color: '#f2b72e', bg: '#fef6e1', text: '#8a6200' },
    PROGRAMADO_CIRCULAR:   { label: 'Programado circular',                    short: 'Prog. circular',   color: '#4cb36a', bg: '#e8f6ec', text: '#21703a' },
}

const PRODUCT_COLUMNS = [
    { field: 'corn',      label: 'Milho'    },
    { field: 'soy',       label: 'Soja'     },
    { field: 'cellulose', label: 'Celulose' },
    { field: 'meal',      label: 'Farelo'   },
    { field: 'sugar',     label: 'Açúcar'   },
]

// Slots de uso emergencial (ZPG): linhas destacadas em âmbar.
const HIGHLIGHTED_SLOTS = ['ZPG_4', 'ZPG']

// Ordem das células editáveis numa linha (é a ordem do Tab).
const EDIT_ORDER = ['train', ...PRODUCT_COLUMNS.map(({ field }) => field), 'terminalId', 'status', 'action', 'reactionTime']

const STATUS_OPTIONS = Object.entries(TRAIN_QUEUE_STATUS).map(([value, { label }]) => ({ value, label }))

const _isEmptyRow = (row) => EDIT_ORDER.every(field => row[field] === null || row[field] === undefined || row[field] === '')

// Com as linhas recolhidas, some toda linha vazia; um slot inteiramente vazio
// fica com uma linha só, para continuar aparecendo como disponível.
const _collapseRows = (rows) => rows.filter((row, index) => {
    if (!_isEmptyRow(row)) return true
    const slotRows = rows.filter(other => other.slotKey === row.slotKey)
    return slotRows.every(_isEmptyRow) && slotRows[0] === rows[index]
})

const _rowTotal = (row) => PRODUCT_COLUMNS.reduce((sum, { field }) => sum + (Number(row[field]) || 0), 0)

const StatusBadge = ({ status, short = true }) => {

    const config = TRAIN_QUEUE_STATUS[status]

    if (!config) return null

    return (
        <span
            className="inline-flex items-center gap-[6px] px-[9px] py-[3px] rounded-full text-[0.7rem] font-semibold whitespace-nowrap"
            style={{ backgroundColor: config.bg, color: config.text }}
        >
            <span className="w-[7px] h-[7px] rounded-full" style={{ backgroundColor: config.color }} />
            {short ? config.short : config.label}
        </span>
    )
}

const cardClass = "bg-white rounded-[10px] border border-[#e3e8ef] shadow-[0_1px_2px_rgba(0,42,104,0.06),0_8px_24px_rgba(0,42,104,0.06)]"

// Quantas linhas o modo recolhido esconde (para o texto do botão na página).
export const countCollapsedRows = (rows) => rows.length - _collapseRows(rows).length

export const TrainQueueLegend = () => (

    <aside className={`${cardClass} flex flex-col gap-3 p-4 min-w-[260px]`}>

        <h3 className="text-[0.7rem] font-bold text-azul-profundo-e uppercase tracking-[0.08em]">Legenda do status</h3>

        {Object.keys(TRAIN_QUEUE_STATUS).map(status => (
            <StatusBadge key={status} status={status} short={false} />
        ))}

    </aside>
)

// Tabela da Fila de Trens. Os slots são fixos; cada slot ocupa uma ou mais linhas
// (rowSpan na coluna Slot). Totais por linha e por coluna são calculados aqui.
// isCollapsed (controlado pela página) esconde as linhas vazias.
function TrainQueueTable({ rows: allRows, isCollapsed, terminalOptions, canEdit, onSaveCell }) {

    // Célula em edição: { rowIndex, field } ou null (rowIndex nas linhas visíveis).
    const [ _editing, _setEditing ] = useState(null)

    const rows = isCollapsed ? _collapseRows(allRows) : allRows

    // Recolher/expandir muda os índices das linhas; fecha a edição em andamento.
    useEffect(() => { _setEditing(null) }, [isCollapsed])

    // Props de edição de uma célula; Tab no fim da linha segue para a próxima linha.
    const _editProps = (rowIndex, field) => ({
        canEdit,
        isEditing:   _editing?.rowIndex === rowIndex && _editing?.field === field,
        onStartEdit: () => _setEditing({ rowIndex, field }),
        onStopEdit:  () => _setEditing(null),
        onNavigate:  (step) => {
            const flat = rowIndex * EDIT_ORDER.length + EDIT_ORDER.indexOf(field) + step
            const last = rows.length * EDIT_ORDER.length - 1
            if (flat < 0 || flat > last) return _setEditing(null)
            _setEditing({ rowIndex: Math.floor(flat / EDIT_ORDER.length), field: EDIT_ORDER[flat % EDIT_ORDER.length] })
        },
    })

    // Quantas linhas cada slot ocupa, para o rowSpan da primeira linha do slot.
    const slotSizes = rows.reduce((sizes, row) => ({ ...sizes, [row.slotKey]: (sizes[row.slotKey] || 0) + 1 }), {})

    // Fundo alternado por slot (não por linha), para os grupos ficarem nítidos.
    const slotOrder = [...new Set(rows.map(row => row.slotKey))]

    const columnTotals = Object.fromEntries(PRODUCT_COLUMNS.map(({ field }) => [
        field, rows.reduce((sum, row) => sum + (Number(row[field]) || 0), 0)
    ]))

    const grandTotal = Object.values(columnTotals).reduce((sum, value) => sum + value, 0)

    const headerCell = "bg-azul-profundo-m text-white text-[0.7rem] font-semibold uppercase tracking-[0.06em] px-3 border-r border-white/15 last:border-r-0"

    return (

        <div className={`${cardClass} overflow-hidden`}>

            <div className="overflow-x-auto">

                <table className="w-full border-separate border-spacing-0 text-[0.8rem] text-cinza-chumbo-e tabular-nums">

                    <thead>
                        <tr>
                            <th rowSpan={2} className={`${headerCell} w-[150px] text-left py-3`}>Slot</th>
                            <th rowSpan={2} className={`${headerCell} w-[80px]`}>Trem</th>
                            <th colSpan={PRODUCT_COLUMNS.length} className={`${headerCell} pt-[9px] pb-[5px] border-b border-white/15`}>
                                Vagões por produto
                            </th>
                            <th rowSpan={2} className={`${headerCell} w-[70px]`}>Total</th>
                            <th rowSpan={2} className={`${headerCell} w-[100px]`}>Terminal</th>
                            <th rowSpan={2} className={`${headerCell} w-[140px]`}>Status</th>
                            <th rowSpan={2} className={`${headerCell} text-left`}>Ação</th>
                            <th rowSpan={2} className={`${headerCell} w-[120px]`}>Tempo de Reação</th>
                        </tr>
                        <tr>
                            {PRODUCT_COLUMNS.map(({ field, label }) => (
                                <th key={field} className={`${headerCell} w-[70px] py-[6px] font-medium normal-case tracking-normal text-[0.75rem] text-white/90`}>{label}</th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>

                        {rows.map((row, index) => {

                            const isFirstOfSlot = index === 0 || rows[index - 1].slotKey !== row.slotKey
                            const isHighlighted = HIGHLIGHTED_SLOTS.includes(row.slotKey)
                            const groupBg       = isHighlighted ? 'bg-[#fff8e6]' : slotOrder.indexOf(row.slotKey) % 2 === 0 ? 'bg-white' : 'bg-[#f8fafd]'
                            const hoverBg       = isHighlighted ? 'hover:bg-[#fdf0cc]' : 'hover:bg-[#f1f6fd]'
                            const save          = (field) => (value) => onSaveCell(row.id, field, value)

                            return (

                                <tr 
                                    key={row.id} 
                                    className={`${groupBg} ${hoverBg} transition-colors ${isFirstOfSlot && index > 0 ? '[&>td]:border-t-2 [&>td]:border-t-[#d5deea]' : ''}`}
                                >

                                    {isFirstOfSlot && (
                                        <td
                                            rowSpan={slotSizes[row.slotKey]}
                                            className={`px-3 py-[6px] border-b border-r border-[#dfe6ef] align-middle ${isHighlighted ? 'bg-[#fdeec4] shadow-[inset_4px_0_0_#f2b72e]' : 'bg-[#f3f6fa]'}`}
                                        >
                                            <div className={`font-bold leading-tight ${isHighlighted ? 'text-[#7a5600]' : 'text-azul-profundo-e'}`}>{row.slotName}</div>
                                            {row.slotDetail && <div className={`text-[0.68rem] mt-[2px] leading-tight ${isHighlighted ? 'text-[#8a6200]' : 'text-cinza-chumbo-m'}`}>{row.slotDetail}</div>}
                                        </td>
                                    )}

                                    <TrainQueueEditableCell
                                        value={row.train}
                                        {..._editProps(index, 'train')}
                                        onSave={save('train')}
                                        className="bg-azul-ceu-c/70 text-azul-profundo-e font-bold"
                                    />

                                    {PRODUCT_COLUMNS.map(({ field }) => (
                                        <TrainQueueEditableCell
                                            key={field}
                                            type="number"
                                            value={row[field]}
                                            {..._editProps(index, field)}
                                            onSave={save(field)}
                                        />
                                    ))}

                                    <td className="text-center border-b border-r border-[#e6ebf1] bg-[#e9f1fa] text-azul-profundo-e font-bold">
                                        {_rowTotal(row) || <span className="text-cinza-chumbo-c font-normal">–</span>}
                                    </td>

                                    <TrainQueueEditableCell
                                        type="select"
                                        value={row.terminalId}
                                        display={row.ownerTerminal?.name}
                                        options={terminalOptions}
                                        {..._editProps(index, 'terminalId')}
                                        onSave={(value) => onSaveCell(row.id, 'terminalId', value === null ? null : Number(value))}
                                        className="font-medium"
                                    />

                                    <TrainQueueEditableCell
                                        type="select"
                                        value={row.status}
                                        display={row.status ? <StatusBadge status={row.status} /> : null}
                                        title={TRAIN_QUEUE_STATUS[row.status]?.label}
                                        options={STATUS_OPTIONS}
                                        {..._editProps(index, 'status')}
                                        onSave={save('status')}
                                    />

                                    <TrainQueueEditableCell
                                        value={row.action}
                                        align="left"
                                        {..._editProps(index, 'action')}
                                        onSave={save('action')}
                                    />

                                    <TrainQueueEditableCell
                                        value={row.reactionTime}
                                        {..._editProps(index, 'reactionTime')}
                                        onSave={save('reactionTime')}
                                        className="border-r-0"
                                    />

                                </tr>
                            )
                        })}

                    </tbody>

                    <tfoot>
                        <tr className="text-center font-bold text-azul-profundo-e bg-azul-ceu-c">
                            <td colSpan={2} className="px-3 py-[10px] text-left text-[0.7rem] uppercase tracking-[0.08em] border-r border-white/60">Total geral</td>
                            {PRODUCT_COLUMNS.map(({ field }) => <td key={field} className="border-r border-white/60">{columnTotals[field]}</td>)}
                            <td className="bg-azul-profundo-m text-white">{grandTotal}</td>
                            <td colSpan={4} className="bg-[#f3f6fa]" />
                        </tr>
                    </tfoot>

                </table>

            </div>

        </div>
    )
}

export default TrainQueueTable
